import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { StarIcon, PlusIcon } from '@heroicons/react/24/outline';
import PageContainer from '../components/layout/PageContainer';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import AddBusinessModal from '../components/AddBusinessModal';

interface Business {
  id: string;
  name: string;
  image: string;
  stars: number;
  reviews: number;
  description: string;
  address: string;
  category: string; 
  phone: string;
  isFavorite?: boolean;
  premiumType?: 'gold' | 'silver' | 'bronze';
}

interface Category {
  id: string;
  name: string;
  emoji: string;
  description: string;
  enabled: boolean;
}

// Add image cache
const imageCache: { [key: string]: HTMLImageElement } = {};
const fallbackCache: { [key: string]: HTMLImageElement } = {};

// Preload image function
const preloadImage = (src: string, category: string): Promise<void> => {
  // Check both caches first
  if (imageCache[src]) {
    return Promise.resolve();
  }
  if (fallbackCache[category]) {
    imageCache[src] = fallbackCache[category];
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      imageCache[src] = img;
      resolve();
    };
    img.onerror = () => {
      // If original image fails, try fallback
      const fallbackSrc = `https://getimage-b7jrylvg3a-uc.a.run.app/categories/${category}.png`;
      
      // Check if fallback is already cached
      if (fallbackCache[category]) {
        imageCache[src] = fallbackCache[category];
        resolve();
        return;
      }

      const fallbackImg = new Image();
      fallbackImg.onload = () => {
        fallbackCache[category] = fallbackImg;
        imageCache[src] = fallbackImg;
        resolve();
      };
      fallbackImg.onerror = () => {
        // If fallback also fails, use a default image
        const defaultImg = new Image();
        defaultImg.onload = () => {
          fallbackCache[category] = defaultImg;
          imageCache[src] = defaultImg;
          resolve();
        };
        defaultImg.onerror = reject;
        defaultImg.src = 'https://getimage-b7jrylvg3a-uc.a.run.app/categories/default.png';
      };
      fallbackImg.src = fallbackSrc;
    };
    img.src = src;
  });
};

// Add shimmer effect component with animation
const ShimmerEffect = () => (
  <div className="relative w-full h-full overflow-hidden">
    <div className="absolute inset-0 animate-shimmer bg-gradient-to-r from-white/5 via-white/10 to-white/5 bg-[length:200%_100%]" />
  </div>
);

const BusinessList = () => {
  const { categoryId } = useParams();
  const { user } = useAuth();
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [loadedImages, setLoadedImages] = useState<{ [key: string]: boolean }>({});
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const fetchBusinesses = async () => {
    try {
      const response = await fetch('https://getbusinesslist-b7jrylvg3a-uc.a.run.app');
      const data = await response.json();
      
      let filteredBusinesses = data;
      
      // If we have a categoryId, filter businesses by category
      if (categoryId) {
        if (categoryId.toLowerCase() === 'favorites' && user?.token) {
          const response = await fetch('https://us-central1-appdizquetem.cloudfunctions.net/listFavorites', {
            headers: {
              'Authorization': `Bearer ${user.token}`
            }
          });
          const favorites = await response.json();
          filteredBusinesses = favorites;
        } else {
          filteredBusinesses = data.filter((business: Business) => 
            business.category.toLowerCase() === categoryId.toLowerCase()
          );
        }
      }
      
      // Sort businesses by premium status and rating
      const sortedBusinesses = filteredBusinesses.sort((a: Business, b: Business) => {
        // First sort by premium status
        const premiumOrder = { gold: 0, silver: 1, bronze: 2, none: 3, '': 3, undefined: 3 };
        const aPremium = a.premiumType || 'none';
        const bPremium = b.premiumType || 'none';
        const premiumDiff = premiumOrder[aPremium as keyof typeof premiumOrder] - premiumOrder[bPremium as keyof typeof premiumOrder];
        
        if (premiumDiff !== 0) return premiumDiff;
        
        // Then sort by rating within each premium tier
        const ratingA = a.reviews > 0 ? a.stars / a.reviews : 0;
        const ratingB = b.reviews > 0 ? b.stars / b.reviews : 0;
        
        // If ratings are equal, sort by number of reviews
        if (ratingA === ratingB) {
          return b.reviews - a.reviews;
        }
        
        return ratingB - ratingA;
      });
      
      setBusinesses(sortedBusinesses);
      setLoading(false);

      // Optimize image preloading by grouping by category
      const categoryGroups = sortedBusinesses.reduce((acc: { [key: string]: string[] }, business: Business) => {
        if (!acc[business.category]) {
          acc[business.category] = [];
        }
        acc[business.category].push(business.image);
        return acc;
      }, {});

      // Preload images in parallel by category
      const preloadPromises = Object.entries(categoryGroups as { [key: string]: string[] }).map(([category, images]) => {
        return Promise.all(images.map((src: string) => preloadImage(src, category)));
      });

      Promise.all(preloadPromises)
        .then(() => setImagesLoaded(true))
        .catch(error => {
          console.error('Error preloading images:', error);
          setImagesLoaded(true); // Still set to true to show content
        });
    } catch (error) {
      console.error('Error loading businesses:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch('/categories.json');
        const data = await response.json();
        setCategories(data);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };

    fetchCategories();
    fetchBusinesses();
  }, [categoryId, user?.token]);

  const handleImageLoad = (imageUrl: string) => {
    setLoadedImages(prev => ({
      ...prev,
      [imageUrl]: true
    }));
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-between mb-6">
          <div className="h-8 w-48 rounded-lg">
            <ShimmerEffect />
          </div>
          <div className="h-4 w-32 rounded-lg">
            <ShimmerEffect />
          </div>
        </div>
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, index) => (
            <div
              key={index}
              className="block bg-white/10 backdrop-blur-lg rounded-xl overflow-hidden"
            >
              <div className="flex gap-3 p-3">
                <div className="w-20 h-20 rounded-lg overflow-hidden">
                  <ShimmerEffect />
                </div>
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="h-4 w-3/4 rounded">
                    <ShimmerEffect />
                  </div>
                  <div className="h-3 w-1/4 rounded">
                    <ShimmerEffect />
                  </div>
                  <div className="h-3 w-2/3 rounded">
                    <ShimmerEffect />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">
          {categoryId ? (
            categories.find(cat => cat.id === categoryId)?.name || 'Estabelecimentos'
          ) : (
            'Todos os Estabelecimentos'
          )}
        </h1>
        {user?.accessLevel && user.accessLevel >= 1 && (
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors"
          >
            <PlusIcon className="h-5 w-5" />
            Adicionar Novo
          </button>
        )}
      </div>

      {categoryId?.toLowerCase() === 'favorites' && !user ? (
        <div className="text-center py-8">
          <p className="text-white/60 mb-4">Faça login para ver seus favoritos</p>
          <Link 
            to="/login" 
            className="inline-block bg-white/10 hover:bg-white/20 text-white px-6 py-2 rounded-lg transition-colors"
          >
            Fazer Login
          </Link>
        </div>
      ) : businesses.length === 0 ? (
        <div className="text-center text-white/60 py-8">
          Nenhum estabelecimento encontrado nesta categoria.
        </div>
      ) : (
        <div className="space-y-3">
          {businesses.map((business) => (
            <Link
              key={business.id}
              to={`/business/${business.id}`}
              className={`block backdrop-blur-lg rounded-xl overflow-hidden hover:bg-white/20 transition-colors relative ${
                business.premiumType === 'gold' || business.premiumType === 'silver'
                  ? 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/50'
                  : 'bg-white/10'
              }`}
            >
              {(business.premiumType === 'gold' || business.premiumType === 'silver') && (
                <div className="absolute top-2 right-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs px-2 py-1 rounded-full">
                  Destaque
                </div>
              )}
              <div className="flex gap-3 p-3">
                <div className="w-20 h-20 rounded-lg overflow-hidden relative">
                  {!loadedImages[business.image] && (
                    <div className="absolute inset-0">
                      <ShimmerEffect />
                    </div>
                  )}
                  <img 
                    src={imageCache[business.image]?.src || business.image} 
                    alt={business.name}
                    className={`w-full h-full object-cover ${!loadedImages[business.image] ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
                    onLoad={() => handleImageLoad(business.image)}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      const fallbackSrc = `https://getimage-b7jrylvg3a-uc.a.run.app/categories/${business.category}.png`;
                      if (fallbackCache[business.category]) {
                        target.src = fallbackCache[business.category].src;
                      } else {
                        target.src = fallbackSrc;
                      }
                    }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-white font-medium truncate">
                      {business.name}
                    </h3>
                  </div>
                  <div className="flex items-center gap-1 mb-1">
                    <StarIcon className="h-4 w-4 text-yellow-400" />
                    <span className="text-white/80 text-sm">{business.stars === 0 ? '0' : (business.stars/business.reviews).toFixed(1)}</span>
                    <span className="text-white/60 text-sm">({business.reviews})</span>
                  </div>
                  <p className="text-white/60 text-sm truncate">
                    {business.description}
                  </p>
                  <div className="mt-1 text-white/40 text-xs">
                    {business.address.split('\n')[0]}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      <AddBusinessModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={(businessId) => {
          // Refresh the business list
          fetchBusinesses();
        }}
      />
    </PageContainer>
  );
};

export default BusinessList; 